/**
 * §7.2 screen 2 — Inbox.
 *
 * Server-side paginated by opaque cursor. Free-text search is over `reference`
 * ONLY: searching citizens' appeal text is a v2 decision with its own privacy
 * review, so this screen offers no such box and the contract offers no such
 * parameter.
 *
 * The list response carries no full phone and no appeal body by construction
 * (`AdminMurojaatListItem`) — only `phone_tail`, which is the one part of the
 * number a citizen is ever shown. There is deliberately no column for either.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CHANNELS,
  MUROJAAT_KINDS,
  MUROJAAT_STATUSES,
  type Channel,
  type MurojaatKind,
  type MurojaatStatus,
} from '@navoiy/domain';
import type { components } from '@navoiy/api-types';
import { ApiError, api, errorKey, unwrap } from '../api/client.js';
import { useSession } from '../auth/SessionContext.js';
import { useI18n } from '../i18n/LocaleContext.jsx';
import { formatDate, formatDateTime, fromDateInput } from '../lib/format.js';
import { useMahallas } from '../lib/useMahallas.js';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorBanner,
  Field,
  Select,
  Spinner,
  Table,
  Td,
  Th,
  TextInput,
  Tr,
} from '../ui/index.jsx';

type ListItem = components['schemas']['AdminMurojaatListItem'];

interface Filters {
  status: MurojaatStatus | '';
  mahallaId: string;
  kind: MurojaatKind | '';
  channel: Channel | '';
  q: string;
  from: string;
  to: string;
  overdue: boolean;
}

const EMPTY_FILTERS: Filters = {
  status: '',
  mahallaId: '',
  kind: '',
  channel: '',
  q: '',
  from: '',
  to: '',
  overdue: false,
};

const STATUS_TONE: Record<MurojaatStatus, 'neutral' | 'brand' | 'success' | 'gold'> = {
  new: 'gold',
  in_review: 'brand',
  answered: 'success',
  closed: 'neutral',
};

export function InboxScreen(): React.JSX.Element {
  const { t, locale } = useI18n();
  const { expire } = useSession();
  const navigate = useNavigate();
  const { mahallas } = useMahallas(locale);

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [items, setItems] = useState<ListItem[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /**
   * A language switch re-reads the list FROM THE TOP.
   *
   * The loader appends while a cursor is set, so simply re-running the query in
   * the new language would fetch the same page again and show every row on
   * screen twice. Adjusting the state during render (rather than in an effect)
   * is what keeps that stale cursor from ever reaching a request: React discards
   * this render and recomputes `query` with the cursor already cleared, so the
   * switch costs exactly one fetch.
   */
  const [listLocale, setListLocale] = useState(locale);
  if (listLocale !== locale) {
    setListLocale(locale);
    setCursor(undefined);
    setItems([]);
  }

  const query = useMemo(() => {
    const q: Record<string, string | boolean | number> = {};
    if (applied.status) q.status = applied.status;
    if (applied.mahallaId) q.mahalla_id = applied.mahallaId;
    if (applied.kind) q.kind = applied.kind;
    if (applied.channel) q.channel = applied.channel;
    if (applied.q) q.q = applied.q;
    const from = fromDateInput(applied.from);
    const to = fromDateInput(applied.to, true);
    if (from) q.from = from;
    if (to) q.to = to;
    if (applied.overdue) q.overdue = true;
    if (cursor) q.cursor = cursor;
    // `mahalla_name` is the one server-rendered string in this table, so the
    // panel has to say which language it is reading in. In `locale` because the
    // dependency list below re-runs the load on a language switch — the column
    // follows the switcher instead of waiting for the next filter change.
    q.locale = locale;
    return q;
  }, [applied, cursor, locale]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = unwrap(
        await api().GET('/admin/murojaats', {
          params: { query: query as never },
        }),
      );
      setItems((previous) => (cursor ? [...previous, ...data.items] : data.items));
      setNextCursor(data.next_cursor);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        expire();
        return;
      }
      const { key, values } = errorKey(caught);
      setError(t(key, values));
    } finally {
      setLoading(false);
    }
  }, [query, cursor, expire, t]);

  useEffect(() => {
    void load();
  }, [load]);

  function applyFilters(): void {
    setCursor(undefined);
    setItems([]);
    setApplied(filters);
  }

  function resetFilters(): void {
    setFilters(EMPTY_FILTERS);
    setCursor(undefined);
    setItems([]);
    setApplied(EMPTY_FILTERS);
  }

  return (
    <div className="flex flex-col gap-5">
      <Card
        title={t('admin.inbox.title')}
        actions={
          <div className="flex gap-2">
            <Button tone="secondary" onClick={resetFilters}>
              {t('admin.inbox.filterReset')}
            </Button>
            <Button onClick={applyFilters}>{t('admin.common.apply')}</Button>
          </div>
        }
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Field label={t('admin.inbox.searchPlaceholder')}>
            <TextInput
              value={filters.q}
              maxLength={32}
              placeholder="NV-20260812-K7M2"
              onChange={(event) => setFilters({ ...filters, q: event.target.value })}
            />
          </Field>

          <Field label={t('admin.inbox.filterStatus')}>
            <Select
              value={filters.status}
              onChange={(event) =>
                setFilters({ ...filters, status: event.target.value as MurojaatStatus | '' })
              }
            >
              <option value="">{t('admin.common.all')}</option>
              {MUROJAAT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {t(`shared.murojaat.status.${status}`)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('admin.inbox.filterMahalla')}>
            <Select
              value={filters.mahallaId}
              onChange={(event) => setFilters({ ...filters, mahallaId: event.target.value })}
            >
              <option value="">{t('admin.common.all')}</option>
              {mahallas.map((mahalla) => (
                <option key={mahalla.id} value={mahalla.id}>
                  {mahalla.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('admin.inbox.filterKind')}>
            <Select
              value={filters.kind}
              onChange={(event) =>
                setFilters({ ...filters, kind: event.target.value as MurojaatKind | '' })
              }
            >
              <option value="">{t('admin.common.all')}</option>
              {MUROJAAT_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`shared.murojaat.kind.${kind}`)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('admin.inbox.filterChannel')}>
            <Select
              value={filters.channel}
              onChange={(event) =>
                setFilters({ ...filters, channel: event.target.value as Channel | '' })
              }
            >
              <option value="">{t('admin.common.all')}</option>
              {CHANNELS.map((channel) => (
                <option key={channel} value={channel}>
                  {t(`admin.channel.${channel}`)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label={t('admin.inbox.filterFrom')}>
            <TextInput
              type="date"
              value={filters.from}
              onChange={(event) => setFilters({ ...filters, from: event.target.value })}
            />
          </Field>

          <Field label={t('admin.inbox.filterTo')}>
            <TextInput
              type="date"
              value={filters.to}
              onChange={(event) => setFilters({ ...filters, to: event.target.value })}
            />
          </Field>

          <div className="flex items-end pb-2">
            <Checkbox
              label={t('admin.inbox.filterOverdue')}
              checked={filters.overdue}
              onChange={(event) => setFilters({ ...filters, overdue: event.target.checked })}
            />
          </div>
        </div>
      </Card>

      <Card>
        <ErrorBanner message={error} />

        {items.length === 0 && !loading ? (
          <EmptyState message={t('admin.inbox.empty')} />
        ) : (
          <Table
            head={
              <tr>
                <Th>{t('admin.column.reference')}</Th>
                <Th>{t('admin.column.mahalla')}</Th>
                <Th>{t('admin.column.kind')}</Th>
                <Th>{t('admin.column.channel')}</Th>
                {/* §14.8 screen 2 — the Art. 6 classification, migration 0020. */}
                <Th>{t('admin.column.legalType')}</Th>
                <Th>{t('admin.column.received')}</Th>
                <Th>{t('admin.column.due')}</Th>
                <Th>{t('admin.column.status')}</Th>
              </tr>
            }
          >
            {items.map((item) => (
              <Tr key={item.id} onClick={() => navigate(`/murojaats/${item.id}`)}>
                <Td className="whitespace-nowrap font-semibold tracking-ref text-brand-800">
                  {item.reference}
                </Td>
                <Td>{item.mahalla_name}</Td>
                <Td>
                  <span className="flex items-center gap-2">
                    {t(`shared.murojaat.kind.${item.kind}`)}
                    {item.has_media ? <Badge tone="brand">{t('admin.inbox.hasMedia')}</Badge> : null}
                  </span>
                </Td>
                <Td>{t(`admin.channel.${item.channel}`)}</Td>
                {/*
                  Unclassified is rendered as the WORD, greyed — not as a blank
                  cell. §14.3.4's rule applied to a row: an appeal with no
                  determination yet is a fact, and an empty cell says nothing
                  about whether one is missing or the column is broken.
                */}
                <Td>
                  {item.legal_type ? (
                    t(`admin.legalType.${item.legal_type}`)
                  ) : (
                    <span className="text-ink-400">{t('admin.legalType.unclassified')}</span>
                  )}
                </Td>
                <Td className="whitespace-nowrap">{formatDateTime(item.received_at)}</Td>
                <Td className="whitespace-nowrap">
                  {item.is_overdue ? (
                    <span className="font-semibold text-danger-500">
                      {formatDate(item.due_at)}
                    </span>
                  ) : (
                    formatDate(item.due_at)
                  )}
                </Td>
                <Td>
                  <Badge tone={STATUS_TONE[item.status]}>
                    {t(`shared.murojaat.status.${item.status}`)}
                  </Badge>
                </Td>
              </Tr>
            ))}
          </Table>
        )}

        {loading ? <Spinner label={t('admin.common.loading')} /> : null}

        {nextCursor && !loading ? (
          <div className="pt-4 text-center">
            <Button tone="secondary" onClick={() => setCursor(nextCursor)}>
              {t('admin.inbox.loadMore')}
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
